import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ReauthDialog } from '@/components/ReauthDialog';
import { lazyComponent } from '@/app/lazyWithPreload';
import { useMountedOnce } from '@/hooks/useMountedOnce';
import { SessionBanner } from '@/components/SessionBanner';
import { useConnectionStatus } from '@/hooks/useConnectionStatus';
import { useKeepMailOnDevice } from '@/offline/keepOnDevice';
import { SessionBootSkeleton, useSession } from './session';
import '@/components/session.css';

/*
 * Only on the sign-out-with-unsent-mail path, so not on the first paint
 * (#577 follow-up). Mounted from the first time it is asked for, and kept, so
 * it closes and hands focus back as before.
 */
const SignOutOutboxDialog = lazyComponent(
  () => import('@/components/SignOutOutboxDialog'),
  'SignOutOutboxDialog',
);

/**
 * Builds the `/login` URL that remembers where the user was heading.
 *
 * Exported because the login view has to read it back, and a return path that
 * one side encodes and the other parses by hand is a bug waiting to happen.
 */
export function loginUrlFor(pathname: string, search: string): string {
  const from = `${pathname}${search}`;
  if (from === '/' || from === '') return '/login';
  return `/login?from=${encodeURIComponent(from)}`;
}

/**
 * Sanitises a `?from=` value before navigating to it.
 *
 * A return path is attacker-controllable (it is in a URL someone can send you),
 * so anything that is not a plain in-app path is discarded. `//evil.test` is a
 * protocol-relative URL, not a path — that is the case this exists to catch.
 */
export function safeReturnPath(raw: string | null, fallback = '/mail'): string {
  if (raw === null || raw === '') return fallback;
  if (!raw.startsWith('/') || raw.startsWith('//')) return fallback;
  return raw;
}

/**
 * Route guard.
 *
 * A component wrapper rather than a router `loader`, so the shell paints during
 * the check instead of the route hanging. Three rules:
 *
 * - **Never redirect before bootstrap resolves**, or every cold start flashes
 *   the login screen.
 * - **Never redirect on `expired`.** That is the state with a half-written
 *   message in it; it gets the modal, over the still-mounted app.
 * - **Never redirect on `unavailable`.** A 503 from a datastore outage is not
 *   a sign-out, and treating it as one throws away the user's work over an
 *   outage they had nothing to do with.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { status, binding, signOutPrompt } = useSession();
  const signOutMounted = useMountedOnce(signOutPrompt !== null);
  const location = useLocation();

  if (status === 'bootstrapping') return <SessionBootSkeleton />;

  if (status === 'anonymous') {
    // The deep link survives the round trip: /mail/inbox/m-42 comes back after
    // sign-in instead of dumping the user in the inbox.
    return <Navigate to={loginUrlFor(location.pathname, location.search)} replace />;
  }

  if (status === 'recovery') {
    // A live cookie with no device key, found at boot (ADR 0042). Emphatically
    // NOT a redirect to `/login`: the session is alive, and bouncing the user
    // to a sign-in page would tell them they are signed out when they are not
    // — and would be a logout loop for anyone whose browser keeps evicting the
    // key. Skeleton behind, dialog in front, password once.
    return (
      <>
        <SessionBootSkeleton />
        {/* Sign out is one of this dialog's two buttons, so the Outbox question
            can be raised from here — and when it is, it replaces the prompt
            behind it rather than stacking a second scrim on top of it. */}
        {signOutPrompt === null ? <ReauthDialog mode="recovery" /> : <SignOutOutboxDialog />}
      </>
    );
  }

  if (status === 'unavailable') {
    // Cold start that never got an answer: there is no cached shell state to
    // preserve, but there is also no evidence the user is signed out.
    return <ServiceUnavailableNotice />;
  }

  return (
    <>
      <SessionBanner />
      {children}
      {status === 'expired' && signOutPrompt === null && <ReauthDialog />}
      {/*
       * Cookies alive, device key gone (ADR 0042) — the common case on a
       * 30-day session. `children` stays mounted, so the half-written reply,
       * the scroll position and the selection all survive; a redirect to
       * `/login` here would be a data-loss bug wearing a security costume.
       * Rendered only when the session is otherwise healthy: `expired` owns
       * its own dialog and showing both would stack two scrims.
       */}
      {status === 'authenticated' && binding.recoveryNeeded && signOutPrompt === null && (
        <ReauthDialog mode="recovery" />
      )}
      {/*
       * Signing out with unsent mail on this device (ADR 0058, ADR 0060 §3).
       * Mounted by the guard rather than beside each of the three Sign out
       * buttons, so there is one gate and not three chances to forget it.
       */}
      {signOutMounted && <SignOutOutboxDialog />}
    </>
  );
}

/**
 * The cold start that got no answer — and which of the three true things to
 * say about it.
 *
 * "The server is reachable but cannot serve mail right now" is the right
 * sentence for exactly one of them. Said to someone whose browser knows there
 * is no network it is simply false, and said to someone who turned off "Keep
 * mail on this device" (ADR 0060 §4) it hides the one fact they can act on:
 * there is nothing here to read because they asked for nothing to be kept.
 * An app that pretends to be offline-capable to that person is the thing this
 * setting exists not to do.
 */
export function ServiceUnavailableNotice() {
  const status = useConnectionStatus();
  const [keep] = useKeepMailOnDevice();
  const offline = status === 'offline';

  const copy = !offline
    ? {
        title: 'Mail is temporarily unavailable',
        detail:
          'The server is reachable but cannot serve mail right now. We are retrying — you have not been signed out.',
      }
    : keep
      ? {
          title: 'You’re offline, and nothing is kept here yet',
          detail:
            'Mail you have already opened is kept on this device for offline reading, and none has been yet. It will load as soon as you are back on a network — you have not been signed out.',
        }
      : {
          title: 'Mail isn’t kept on this device',
          detail:
            'You’re offline, and you turned off “Keep mail on this device for offline use”, so there is no copy here to read. Reconnect, or change it in Settings → Privacy. You have not been signed out.',
        };

  return (
    <main className="session-unavailable">
      <section
        className="session-unavailable__card card"
        role="status"
        data-testid="session-unavailable"
        data-kept-on-device={keep ? 'true' : 'false'}
        data-connection={status}
      >
        <h1>{copy.title}</h1>
        <p className="muted">{copy.detail}</p>
      </section>
    </main>
  );
}
